import React, { useLayoutEffect, useRef } from "react";
import Reveal from "../public/Reveal";
import { gsap, ScrollTrigger } from "../public/gsap";

// § 02 — "The Fifteen Years". On desktop the section pins and scroll drives a
// timeline of the LIHTC compliance period: a building draws itself, units
// fill as households are certified, inspections leave marks, and JPRO's tools
// stamp in where they do their work. Small screens and reduced motion get a
// static building and a plain vertical list.

const YEAR_WIDTH = 340; // px of track per year
const NOW_X = 300; // "Now" sits inside the timeline so passed years stay readable to its left
const INTRO = 1.5; // timeline units spent drawing the building before year 0

const EVENTS = [
    {
        year: 0,
        title: "Placed in service",
        body: "Construction closes out and the credit period can begin. Every punch item and inspection report becomes part of the record.",
        tool: "ProSpec360",
    },
    {
        year: 1,
        title: "Initial certifications",
        body: "Each household is income-certified before move-in, and the minimum set-aside has to be met by the close of the first credit year.",
        tool: "JPRO Intake",
    },
    {
        year: 2,
        title: "Holding the income mix",
        body: "Under the Average Income Test every unit carries a designated limit from 20% to 80% of AMI, and the average can’t exceed 60%. Every move-out shifts the math.",
        tool: "AIT Lab",
    },
    {
        year: 3,
        title: "Inspection & file review",
        body: "State agencies inspect units and review tenant files at least once every three years. Findings are reported to the IRS on Form 8823.",
        tool: "ProSpec360",
    },
    {
        year: 5,
        title: "Annual owner certifications",
        body: "Every year the owner certifies compliance to the agency, backed by tenant files that have to hold up line by line.",
        tool: "JPRO Intake",
    },
    { year: 6, title: "Inspection & file review", tool: "ProSpec360", mini: true },
    {
        year: 7,
        title: "An over-income household",
        body: "If a household’s income rises above 140% of the limit, the next available unit rule applies. Miss it and the applicable fraction is at risk.",
        tool: "Ask JPRO",
    },
    { year: 9, title: "Inspection & file review", tool: "ProSpec360", mini: true },
    {
        year: 10,
        title: "The rules move",
        body: "New income limits each spring, revised utility allowances, updated agency manuals and inspection standards. The period doesn’t pause for any of it.",
        tool: "Ask JPRO",
    },
    { year: 12, title: "Inspection & file review", tool: "ProSpec360", mini: true },
    {
        year: 15,
        title: "End of the compliance period",
        body: "Fifteen taxable years after the credit period began, the compliance period closes. The extended use commitment carries on.",
        tool: "Ask JPRO",
    },
];

// Building geometry (viewBox 0 0 320 420). Floors numbered from the ground.
const FLOOR_Y = { 1: 330, 2: 280, 3: 230, 4: 180, 5: 130 };
const COL_X = { A: 58, B: 102, C: 146, D: 190, E: 234 };
const UNITS = Object.entries(FLOOR_Y).flatMap(([floor, y]) =>
    Object.entries(COL_X)
        .filter(([col]) => !(floor === "1" && col === "C")) // the entrance
        .map(([col, x]) => ({ id: `${floor}${col}`, x, y }))
);
const INSPECTIONS = { 3: "2C", 6: "4B", 9: "1D", 12: "5E" };
const OVER_INCOME = "3A";

const pad = (n) => String(n).padStart(2, "0");

function Building({ drawn = false }) {
    const stroke = drawn ? {} : { pathLength: 1, strokeDasharray: 1, strokeDashoffset: 1 };
    const unitAt = (id) => UNITS.find((u) => u.id === id);
    return (
        <svg viewBox="0 0 320 420" className="h-full w-full text-ink dark:text-paper" fill="none">
            <defs>
                {/* Architectural hatching for occupied, certified units */}
                <pattern id={`hatch-${drawn ? "s" : "a"}`} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2="4" stroke="currentColor" strokeWidth="1.1" />
                </pattern>
            </defs>
            <g stroke="currentColor" strokeWidth="1.4" strokeLinecap="butt">
                <path className="b-line" d="M0 400 H320" {...stroke} />
                <path className="b-line" d="M40 400 V110 H280 V400" {...stroke} />
                <path className="b-line" d="M32 110 H288 M40 102 H280 V110 M40 102 V110" {...stroke} />
                <path className="b-line" d="M196 102 V72 H252 V102" {...stroke} />
                <path className="b-line" d="M40 316 H280" {...stroke} />
                <path className="b-line" d="M140 400 V352 H180 V400 M130 346 H190" {...stroke} />
                {UNITS.map((u) => (
                    <path key={u.id} className="b-line" d={`M${u.x} ${u.y} h26 v30 h-26 Z`} {...stroke} />
                ))}
            </g>
            {/* Occupied, certified units */}
            {UNITS.map((u) => (
                <rect
                    key={u.id}
                    className="b-unit"
                    data-unit={u.id}
                    x={u.x + 3}
                    y={u.y + 3}
                    width="20"
                    height="24"
                    fill={`url(#hatch-${drawn ? "s" : "a"})`}
                    opacity={drawn ? 0.7 : 0}
                />
            ))}
            {/* Over-income flag */}
            {(() => {
                const u = unitAt(OVER_INCOME);
                return <rect className="b-over" x={u.x + 3} y={u.y + 3} width="20" height="24" fill="#D23A1F" opacity="0" />;
            })()}
            {/* Inspection marks */}
            {Object.entries(INSPECTIONS).map(([year, id]) => {
                const u = unitAt(id);
                return (
                    <path
                        key={year}
                        className="b-inspect text-stamp"
                        data-year={year}
                        d={`M${u.x - 9} ${u.y + 15} a22 24 0 1 0 44 0 a22 24 0 1 0 -44 0`}
                        stroke="currentColor"
                        strokeWidth="1.6"
                        pathLength="1"
                        strokeDasharray="1"
                        strokeDashoffset={drawn ? 0 : 1}
                        opacity={drawn ? 0.5 : 0}
                    />
                );
            })}
        </svg>
    );
}

function ToolStamp({ tool, className = "" }) {
    return (
        <span
            className={`inline-block -rotate-[4deg] border-[1.5px] border-stamp px-2.5 py-1 font-plex-mono text-[11px] font-medium uppercase tracking-[0.16em] text-stamp ${className}`}
        >
            {tool}
        </span>
    );
}

function SectionIntro() {
    return (
        <div className="max-w-xl">
            <p className="mb-5 font-plex-mono text-[12px] uppercase tracking-[0.2em] text-stamp">§ 02 &nbsp;The fifteen years</p>
            <h2 className="font-news text-[38px] leading-[1.05] tracking-[-0.015em] sm:text-[46px]">
                A tax credit is a fifteen-year promise.
            </h2>
            <p className="mt-4 font-plex text-[16px] leading-relaxed text-ink/65 dark:text-paper/65">
                Every LIHTC building carries a compliance period. Here is where JPRO works across it.
            </p>
        </div>
    );
}

function FifteenYears() {
    const rootRef = useRef(null);
    const pinRef = useRef(null);
    const trackRef = useRef(null);
    const counterRef = useRef(null);

    useLayoutEffect(() => {
        const mm = gsap.matchMedia();
        mm.add("(min-width: 1024px) and (prefers-reduced-motion: no-preference)", () => {
            const q = gsap.utils.selector(pinRef);
            const span = 15 * YEAR_WIDTH;

            const tl = gsap.timeline({
                defaults: { ease: "none" },
                scrollTrigger: {
                    trigger: pinRef.current,
                    start: "top top",
                    end: () => `+=${window.innerHeight * 5.5}`,
                    pin: true,
                    scrub: 0.8,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                },
                onUpdate() {
                    const year = Math.min(15, Math.max(0, Math.floor(this.time() - INTRO + 0.15)));
                    if (counterRef.current) counterRef.current.textContent = pad(year);
                },
            });

            // Construction: the building draws itself.
            tl.to(q(".b-line"), { strokeDashoffset: 0, duration: 1.2, ease: "power2.inOut", stagger: { amount: 0.6 } }, 0);
            tl.to(q(".fy-progress"), { scaleX: 1, duration: INTRO + 15 }, 0);
            // The years roll past the "now" line.
            tl.to(trackRef.current, { x: -span, duration: 15 }, INTRO);
            // Initial certifications fill the units, ground floor up.
            tl.to([...q(".b-unit")].reverse(), { opacity: 0.7, duration: 0.5, stagger: { amount: 0.8 } }, INTRO + 0.6);

            EVENTS.forEach((ev, i) => {
                const t = INTRO + ev.year;
                const el = q(`[data-event="${i}"]`)[0];
                if (!el) return;
                tl.fromTo(el, { opacity: 0.25 }, { opacity: 1, duration: 0.5 }, t - 1.4);
                const stamp = el.querySelector(".fy-stamp");
                if (stamp) {
                    // A stamp accelerates into the paper, then settles.
                    tl.fromTo(
                        stamp,
                        { autoAlpha: 0, scale: 1.6, rotate: -12 },
                        { autoAlpha: 1, scale: 1, rotate: -4, duration: 0.22, ease: "power4.in" },
                        t - 0.8
                    );
                }
                if (ev.year < 15) tl.to(el, { opacity: 0.55, duration: 0.5 }, t + 0.7);
            });

            Object.keys(INSPECTIONS).forEach((year) => {
                const mark = q(`.b-inspect[data-year="${year}"]`);
                const t = INTRO + Number(year);
                tl.set(mark, { opacity: 1 }, t - 0.2);
                tl.to(mark, { strokeDashoffset: 0, duration: 0.4, ease: "power2.out" }, t - 0.2);
                tl.to(mark, { opacity: 0.35, duration: 0.6 }, t + 0.8);
            });

            tl.to(q(".b-over"), { opacity: 1, duration: 0.3 }, INTRO + 7 - 0.2);
            tl.to(q(".b-over"), { opacity: 0, duration: 0.6 }, INTRO + 9);
            tl.fromTo(
                q(".fy-final"),
                { autoAlpha: 0, xPercent: -50, scale: 1.8, rotate: -16 },
                { autoAlpha: 1, xPercent: -50, scale: 1, rotate: -8, duration: 0.25, ease: "power4.in" },
                INTRO + 14.8
            );

            return () => tl.scrollTrigger?.kill();
        });
        // Fonts change text metrics; re-measure pins once they're in.
        document.fonts?.ready.then(() => ScrollTrigger.refresh());
        return () => mm.revert();
    }, []);

    return (
        <section ref={rootRef} className="relative">
            {/* Desktop: pinned, scroll-driven */}
            <div className="hidden lg:block lg:motion-reduce:hidden">
                <div ref={pinRef} className="relative flex h-screen min-h-[680px] flex-col overflow-hidden">
                    <div className="relative pt-14 text-ink/80 dark:text-paper/70">
                        <div className="h-px bg-current opacity-25" />
                        <div className="fy-progress absolute left-0 top-14 h-px w-full origin-left scale-x-0 bg-stamp" />
                    </div>

                    <div className="flex items-end justify-between gap-10 pt-10">
                        <SectionIntro />
                        <div className="text-right">
                            <p className="font-plex-mono text-[11px] uppercase tracking-[0.2em] text-ink/50 dark:text-paper/50">Compliance period · year</p>
                            <p className="font-plex-mono text-[120px] font-normal leading-[0.9] tracking-[-0.04em]">
                                <span ref={counterRef}>00</span>
                                <span className="text-[40px] tracking-normal text-ink/35 dark:text-paper/35"> /15</span>
                            </p>
                        </div>
                    </div>

                    <div className="grid min-h-0 flex-1 grid-cols-12 gap-10 pb-12 pt-10">
                        <div className="relative col-span-4 min-h-0">
                            <Building />
                            <div className="fy-final invisible absolute left-1/2 top-[38%] whitespace-nowrap border-[3px] border-double border-stamp bg-paper/90 px-4 py-2 text-center font-plex-mono text-[13px] font-medium uppercase leading-snug tracking-[0.18em] text-stamp dark:bg-ink-950/90">
                                Compliance period
                                <br />
                                complete · Yr 15
                            </div>
                        </div>

                        <div className="relative col-span-8 min-h-0 overflow-hidden [mask-image:linear-gradient(to_right,transparent,#000_10%,#000_80%,transparent)]">
                            {/* "Now" line */}
                            {/* Marks the ruler only, so it never runs through an entry's text */}
                            <div className="absolute top-0 z-10 h-[52px] w-px bg-stamp" style={{ left: NOW_X }} />
                            <div className="absolute top-[52px] z-10 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-stamp" style={{ left: NOW_X + 0.5 }} />
                            <div style={{ left: NOW_X + 10 }} className="absolute top-[42px] z-10 bg-paper pr-1 dark:bg-ink-950 font-plex-mono text-[10px] uppercase tracking-[0.2em] text-stamp">Now</div>

                            <div ref={trackRef} className="absolute inset-y-0" style={{ left: NOW_X, width: 16 * YEAR_WIDTH + 400 }}>
                                {/* Ruler */}
                                <div className="absolute left-0 right-0 top-8 h-px bg-ink/25 dark:bg-paper/25" />
                                {Array.from({ length: 16 * 4 }, (_, i) => (
                                    <div
                                        key={i}
                                        className={`absolute top-8 w-px ${i % 4 === 0 ? "h-4 bg-ink/50 dark:bg-paper/50" : "h-2 bg-ink/20 dark:bg-paper/20"}`}
                                        style={{ left: (i * YEAR_WIDTH) / 4 }}
                                    />
                                ))}
                                {Array.from({ length: 16 }, (_, y) => (
                                    <div
                                        key={y}
                                        className="absolute top-0 pl-2 font-plex-mono text-[11px] text-ink/45 dark:text-paper/45"
                                        style={{ left: y * YEAR_WIDTH }}
                                    >
                                        YR {pad(y)}
                                    </div>
                                ))}

                                {/* Events */}
                                {EVENTS.map((ev, i) => (
                                    <article
                                        key={i}
                                        data-event={i}
                                        className={`absolute pl-5 ${ev.mini ? "top-20 w-[200px]" : "top-20 w-[300px]"}`}
                                        style={{ left: ev.year * YEAR_WIDTH }}
                                    >
                                        <p className="font-plex-mono text-[11px] uppercase tracking-[0.16em] text-ink/50 dark:text-paper/50">Year {pad(ev.year)}</p>
                                        {ev.mini ? (
                                            <>
                                                <p className="mt-2 font-news text-[19px] italic leading-snug text-ink/70 dark:text-paper/70">{ev.title}</p>
                                                <ToolStamp tool={ev.tool} className="fy-stamp invisible mt-4" />
                                            </>
                                        ) : (
                                            <>
                                                <h3 className="mt-2 font-news text-[27px] leading-[1.1] tracking-[-0.01em]">{ev.title}</h3>
                                                <p className="mt-3 font-plex text-[14.5px] leading-relaxed text-ink/70 dark:text-paper/70">{ev.body}</p>
                                                <ToolStamp tool={ev.tool} className="fy-stamp invisible mt-5" />
                                            </>
                                        )}
                                    </article>
                                ))}
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* Small screens and reduced motion: static */}
            <div className="py-16 lg:hidden lg:motion-reduce:block">
                <div className="mb-10 h-px bg-ink/20 dark:bg-paper/20" />
                <SectionIntro />
                <div className="mx-auto my-12 h-72 max-w-xs">
                    <Building drawn />
                </div>
                <ol className="space-y-10">
                    {EVENTS.map((ev) => (
                        <Reveal as="li" key={ev.year} className="border-t border-ink/15 pt-5 dark:border-paper/15">
                            <p className="font-plex-mono text-[11px] uppercase tracking-[0.16em] text-ink/50 dark:text-paper/50">Year {pad(ev.year)}</p>
                            <h3 className={`mt-2 font-news leading-tight ${ev.mini ? "text-[21px] italic text-ink/70 dark:text-paper/70" : "text-[26px]"}`}>{ev.title}</h3>
                            {ev.body && <p className="mt-2 font-plex text-[15px] leading-relaxed text-ink/70 dark:text-paper/70">{ev.body}</p>}
                            <ToolStamp tool={ev.tool} className="mt-4" />
                        </Reveal>
                    ))}
                </ol>
            </div>
        </section>
    );
}

export default FifteenYears;
